import { API_URLS } from '@chessx/shared/api-routes'
import React, { useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import axiosInstance from '../../network/axiosInstance'
import { toastErrorMixin, toastMixin } from '../../utils/interact'
import type { ApiError } from '../../types/api'
import './Tournaments.scss'

const TOURNAMENT_URL = API_URLS.TOURNAMENT_BASE

interface Tournament {
  _id: string
  startDate?: string
  endDate?: string
  createdAt?: string
  winnerList?: unknown[]
}

const formatDate = (value?: string): string => {
  if (!value) return '—'
  const parsed = DateTime.fromISO(new Date(value).toISOString())
  return parsed.isValid ? parsed.toFormat('MMM dd, yyyy') : '—'
}

const getTournamentStatus = (
  startDate?: string,
  endDate?: string
): { label: string; cls: string } => {
  const now = new Date()
  const start = new Date(startDate ?? '')
  const end = new Date(endDate ?? '')
  if (now < start) return { label: 'Upcoming', cls: 'status-upcoming' }
  if (now > end) return { label: 'Ended', cls: 'status-ended' }
  return { label: 'Active', cls: 'status-active' }
}

const Tournaments = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [tournaments, setTournaments] = useState<Tournament[]>([])

  const sortedTournaments = useMemo(
    () =>
      [...tournaments].sort(
        (a, b) => new Date(b.startDate ?? '').getTime() - new Date(a.startDate ?? '').getTime()
      ),
    [tournaments]
  )

  const fetchTournaments = async () => {
    try {
      setIsLoading(true)
      const response = await axiosInstance.get(TOURNAMENT_URL)
      setTournaments(Array.isArray(response?.data?.data) ? response.data.data : [])
    } catch (e) {
      const error = e as ApiError
      toastErrorMixin.fire({ title: error?.response?.data?.error || 'Error fetching tournaments.' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTournaments()
  }, [])

  const handleDeleteTournament = async (tournamentId: string) => {
    if (!window.confirm('Delete this tournament? This cannot be undone.')) return
    try {
      setDeleteId(tournamentId)
      await axiosInstance.delete(`${TOURNAMENT_URL}/deleteTournament/${tournamentId}`)
      setTournaments(prev => prev.filter(t => t._id !== tournamentId))
      toastMixin.fire({ title: 'Tournament deleted successfully.' })
    } catch (e) {
      const error = e as ApiError
      toastErrorMixin.fire({ title: error?.response?.data?.error || 'Error deleting tournament.' })
    } finally {
      setDeleteId(null)
    }
  }

  return (
    <div className="tournaments-page">
      <div className="tournaments-header">
        <div>
          <h3 className="tournaments-title">Tournaments</h3>
          {!isLoading && (
            <span className="tournaments-count">
              {tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button className="btn-refresh" onClick={fetchTournaments} disabled={isLoading}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="tournaments-card">
        {isLoading ? (
          <div className="t-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="t-skel-row">
                <div className="t-skel-cell w20" />
                <div className="t-skel-cell w25" />
                <div className="t-skel-cell w25" />
                <div className="t-skel-cell w15" />
              </div>
            ))}
          </div>
        ) : sortedTournaments.length === 0 ? (
          <div className="t-empty">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#cbd5e1"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="6" />
              <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
            </svg>
            <p>No tournaments found</p>
          </div>
        ) : (
          <div className="t-table-wrap">
            <table className="t-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date Range</th>
                  <th>Status</th>
                  <th>Winners</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedTournaments.map((tournament, index) => {
                  const status = getTournamentStatus(tournament.startDate, tournament.endDate)
                  return (
                    <tr key={tournament._id}>
                      <td className="col-num">{index + 1}</td>
                      <td className="col-dates">
                        <div className="date-range">
                          <span className="date-from">{formatDate(tournament.startDate)}</span>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#94a3b8"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                          <span className="date-to">{formatDate(tournament.endDate)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`t-status ${status.cls}`}>
                          <span className="t-status-dot" />
                          {status.label}
                        </span>
                      </td>
                      <td>
                        <div className="winners-cell">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="8" r="6" />
                            <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
                          </svg>
                          {tournament?.winnerList?.length || 0}
                        </div>
                      </td>
                      <td className="col-created">{formatDate(tournament.createdAt)}</td>
                      <td>
                        <button
                          className="t-delete-btn"
                          disabled={deleteId === tournament._id}
                          onClick={() => handleDeleteTournament(tournament._id)}
                        >
                          {deleteId === tournament._id ? (
                            <>
                              <span className="t-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                              Delete
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Tournaments
